import { WIDGET_CONTROL_TYPE } from '@examdojo/core/widget';
import type { GraphpadWidgetConfig } from '../graphpad.model';

/**
 * Preset demonstrating the function-area feature.
 *
 * Shows a quadratic function with signed area visualization:
 * - Green regions where f(x) > 0 (above x-axis)
 * - Red regions where f(x) < 0 (below x-axis)
 * - Dashed indigo boundary lines at xStart and xEnd
 *
 * For f(x) = x² - 4:
 * - Roots at x = -2 and x = 2
 * - Negative region between roots (below x-axis)
 * - Positive regions outside roots (above x-axis)
 */
export const TEST_INTEGRAL_PRESET: GraphpadWidgetConfig = {
  id: 'test-integral',
  type: 'graphpad',
  params: {
    f: {
      type: 'quadratic-vertex',
      params: { a: 1, h: 0, k: -4 },
    },
    'f-area': {
      type: 'functionArea',
      params: { of: 'f', xStart: -3, xEnd: 3, signed: true },
    },
    'f-roots': {
      type: 'roots',
      params: { of: 'f' },
    },
  },
  display: {
    canvas: { color: 'neutral-0' },
    coordinateSystem: {
      boundingbox: { xStart: -6, xEnd: 6, yStart: -6, yEnd: 8 },
      grid: { type: 'lines', visible: false },
    },
    elements: {
      f: {
        curve: { color: 'indigo', strokeWidth: 3 },
      },
      'f-area': {
        polygon: { fillOpacity: 0.3 },
        line: { color: 'indigo', dash: 3, strokeWidth: 2 },
        subparts: {
          positive: {
            polygon: { fillColor: 'green' },
          },
          negative: {
            polygon: { fillColor: 'red' },
          },
        },
      },
      'f-roots': {
        point: {
          color: 'amber',
          label: { contentVariant: 'name+coord', position: 'top' },
        },
      },
    },
  },
  behavior: {
    controls: [
      {
        id: 'slider-xStart',
        type: WIDGET_CONTROL_TYPE.Slider,
        label: 'xStart',
        param: 'f-area.xStart',
        range: [-5, 5, 0.5],
        defaultValue: -3,
        editable: true,
        color: 'indigo',
      },
      {
        id: 'slider-xEnd',
        type: WIDGET_CONTROL_TYPE.Slider,
        label: 'xEnd',
        param: 'f-area.xEnd',
        range: [-5, 5, 0.5],
        defaultValue: 3,
        editable: true,
        color: 'indigo',
      },
    ],
  },
};
